import {
  Box,
  DialogContent,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Tooltip,
  Typography,
} from "@mui/material";
import Checkbox from "../../../components/ui/Checkbox";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { useTheme } from "@mui/material/styles";
import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useIntl } from "../../../contexts/IntlContext";
import Autocomplete from "../../../components/ui/Autocomplete";
import Input from "../../../components/ui/Input";
import Select from "../../../components/ui/Select";
import LoadingButton from "../../../components/ui/LoadingButton";
import api from "../../../services/api";
import DeviceSelector, { CameraOption } from "./LivePeriod/DeviceSelector";
import { fontWeights } from "../../../themes/fontWeights";
import {
  CameraStatusRow,
  EstablishmentWithChannels,
  LivePlaylistOption,
  LiveTemplateRow,
  YoutubeAccountOption,
} from "../types";

type Privacy = "PUBLIC" | "UNLISTED" | "PRIVATE";

const playlistFilter = createFilterOptions<LivePlaylistOption>({
  stringify: (opt) => `${opt.id} ${opt.name}`,
});

const youtubeAccountFilter = createFilterOptions<YoutubeAccountOption>({
  stringify: (opt) => `${opt.id} ${opt.name}`,
});

const normalizeKeyLoose = (value: string) => {
  return value
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, "")
    .replace(/[^a-z0-9]/g, "");
};

const DEFAULT_BACKGROUND_MUSIC_PATH = "music/playlists/random/";

const normalizeLoose = (value: string) =>
  String(value ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim();

const isScheduleConflictMessage = (message: string) => {
  const normalized = normalizeLoose(message);
  return normalized.includes("periodo escolhido ja possui agendamentos");
};

const isYoutubeTokenExpiredMessage = (message: string) => {
  const normalized = normalizeLoose(message);
  return normalized.includes("invalid or expired token");
};

const getFriendlyScheduleCreateError = (
  intl: ReturnType<typeof useIntl>,
  error: any,
) => {
  const data = error?.response?.data;
  const candidates = [
    typeof data === "string" ? data : "",
    data && typeof data.message === "string" ? data.message : "",
    data && typeof data.error === "string" ? data.error : "",
    typeof error?.message === "string" ? error.message : "",
  ].filter(Boolean);

  const matched = candidates.find((value) => isScheduleConflictMessage(value));
  if (matched)
    return intl.formatMessage({ id: "liveStreams.scheduleConflictPeriod" });

  const youtubeAuthExpired = candidates.find((value) =>
    isYoutubeTokenExpiredMessage(value),
  );
  if (youtubeAuthExpired)
    return intl.formatMessage({ id: "messages.youtubeTokenExpired" });

  return String(
    candidates[0] ?? intl.formatMessage({ id: "global.generic_error" }),
  );
};

const SCHEDULE_DRAFT_KEY = "liveStreams.scheduleDraft.v1";
type ScheduleDraft = {
  establishmentId?: string;
  deviceId?: number;
  channelUuid?: string;
  deviceName?: string;
  liveName?: string;
  liveNameTouched?: boolean;
  privacy?: Privacy;
  enableBackgroundMusic?: boolean;
  playlistId?: number | "";
};

type Props = {
  isAdmin: boolean;
  isLocalhost: boolean;
  establishments: EstablishmentWithChannels[];
  setEstablishments: Dispatch<SetStateAction<EstablishmentWithChannels[]>>;
  establishmentsReady: boolean;
  selectedEstablishmentId: string;
  setSelectedEstablishmentId: (id: string) => void;
  selectedEstablishment: EstablishmentWithChannels | null;
  cameraCourts: CameraStatusRow[];
  templatesLoading: boolean;
  livePlaylists: LivePlaylistOption[];
  setLivePlaylists: Dispatch<SetStateAction<LivePlaylistOption[]>>;
  playlistListContextLiveId: number | null;
};

/**
 * Formulário de criação de Live no modo operacional (ADMIN/SUPPORT, criação/subida imediata, sem
 * agendamento nem LiveSchedule) — o único público deste componente agora. Qualquer criação
 * agendada (OWNER, ou ADMIN/SUPPORT fora de operação) passa por `LivePeriodCreationFlow`, que
 * cobre 1 dia ou vários com o mesmo formulário.
 *
 * Renderizado como conteúdo dentro do `<Dialog>` compartilhado de `LiveCreationDialog` — o
 * título/botão de fechar são do shell, não deste componente.
 */
export default function SingleLiveCreationFlow({
  isAdmin,
  isLocalhost,
  establishments,
  setEstablishments,
  establishmentsReady,
  selectedEstablishmentId,
  setSelectedEstablishmentId,
  selectedEstablishment,
  cameraCourts,
  templatesLoading,
  livePlaylists,
  setLivePlaylists,
  playlistListContextLiveId,
}: Props) {
  const intl = useIntl();
  const theme = useTheme();
  const navigate = useNavigate();
  const selectMenuProps = useMemo(
    () => ({ sx: { zIndex: theme.zIndex.modal + 10 } }),
    [theme.zIndex.modal],
  );

  const [liveTemplates, setLiveTemplates] = useState<LiveTemplateRow[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<number | null>(null);
  const [selectedChannelUuid, setSelectedChannelUuid] = useState<string>("");
  const [selectedDeviceName, setSelectedDeviceName] = useState<string>("");
  const [liveName, setLiveName] = useState<string>("");
  const [liveNameTouched, setLiveNameTouched] = useState(false);

  const [privacy, setPrivacy] = useState<Privacy>("PUBLIC");
  const [enableAudio, setEnableAudio] = useState(false);
  const [enableBackgroundMusic, setEnableBackgroundMusic] = useState(true);
  const [directFinancePayment, setDirectFinancePayment] = useState<boolean>(true);
  const [selectedLivePlaylistId, setSelectedLivePlaylistId] = useState<
    number | ""
  >("");
  const [playlistTouched, setPlaylistTouched] = useState(false);
  const [youtubeAccounts, setYoutubeAccounts] = useState<
    YoutubeAccountOption[]
  >([]);
  const [selectedYoutubeAccountId, setSelectedYoutubeAccountId] = useState<
    number | ""
  >("");
  const [isCreating, setIsCreating] = useState(false);
  const [scheduleDialogStep, setScheduleDialogStep] = useState(0);
  const createInFlightRef = useRef(false);

  // Restore draft when user navigates away and comes back.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(SCHEDULE_DRAFT_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as ScheduleDraft;
      toast.info(intl.formatMessage({ id: "liveStreams.draftRestoredHint" }));
      if (parsed.establishmentId)
        setSelectedEstablishmentId(String(parsed.establishmentId));
      if (
        typeof parsed.deviceId === "number" &&
        Number.isFinite(parsed.deviceId)
      )
        setSelectedDeviceId(parsed.deviceId);
      if (typeof parsed.channelUuid === "string")
        setSelectedChannelUuid(parsed.channelUuid);
      if (typeof parsed.deviceName === "string")
        setSelectedDeviceName(parsed.deviceName);
      if (typeof parsed.liveNameTouched === "boolean")
        setLiveNameTouched(parsed.liveNameTouched);
      if (typeof parsed.liveName === "string") setLiveName(parsed.liveName);
      if (
        parsed.privacy === "PUBLIC" ||
        parsed.privacy === "UNLISTED" ||
        parsed.privacy === "PRIVATE"
      ) {
        setPrivacy(parsed.privacy);
      }
      setEnableAudio(false);
      if (typeof parsed.enableBackgroundMusic === "boolean") {
        setEnableBackgroundMusic(parsed.enableBackgroundMusic);
      }
      if (typeof parsed.playlistId !== "undefined")
        setSelectedLivePlaylistId(parsed.playlistId as any);
    } catch {
      // ignore malformed drafts
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const draft: ScheduleDraft = {
      establishmentId: selectedEstablishmentId || undefined,
      deviceId: selectedDeviceId ?? undefined,
      channelUuid: selectedChannelUuid || undefined,
      deviceName: selectedDeviceName || undefined,
      liveName,
      liveNameTouched,
      privacy,
      enableBackgroundMusic,
      playlistId: selectedLivePlaylistId,
    };

    const timer = window.setTimeout(() => {
      try {
        localStorage.setItem(SCHEDULE_DRAFT_KEY, JSON.stringify(draft));
      } catch {
        // ignore
      }
    }, 150);

    return () => window.clearTimeout(timer);
  }, [
    liveName,
    liveNameTouched,
    privacy,
    enableBackgroundMusic,
    selectedEstablishmentId,
    selectedLivePlaylistId,
    selectedChannelUuid,
    selectedDeviceId,
    selectedDeviceName,
  ]);

  const scheduleStepWhereWhenValid =
    isLocalhost || (!!selectedEstablishmentId && !!selectedDeviceId);
  const scheduleStepConfigurationValid =
    isLocalhost ||
    (typeof selectedLivePlaylistId === "number" && selectedLivePlaylistId > 0);

  useEffect(() => {
    let canceled = false;
    const loadPlaylists = async () => {
      if (!establishmentsReady) return;
      try {
        // Admin sem liveId no state da rota: lista ampla (novo agendamento), a menos que tenha escolhido
        // uma conta YouTube no seletor (restringe às playlists dessa conta). Com focusLiveId vindo da aba
        // Lives, restringe à conta YouTube da live.
        const params: Record<string, number> = {};
        if (playlistListContextLiveId != null)
          params.liveId = playlistListContextLiveId;
        if (isAdmin && selectedYoutubeAccountId !== "")
          params.youtubeAccountId = selectedYoutubeAccountId;
        const response = await api.get<{
          data: Array<{ id: number; name: string }>;
        }>("/app/lives/playlists", {
          ...(Object.keys(params).length ? { params } : {}),
        });
        const rows = Array.isArray(response.data?.data)
          ? response.data.data
          : [];
        const normalized: LivePlaylistOption[] = rows
          .map((r: any) => ({ id: Number(r.id), name: String(r.name ?? r.id) }))
          .filter((r) => Number.isFinite(r.id) && !!r.name);
        normalized.sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
        );
        if (!canceled) setLivePlaylists(normalized);
      } catch {
        if (!canceled) setLivePlaylists([]);
      }
    };
    void loadPlaylists();
    return () => {
      canceled = true;
    };
  }, [
    establishmentsReady,
    playlistListContextLiveId,
    isAdmin,
    selectedYoutubeAccountId,
    setLivePlaylists,
  ]);

  useEffect(() => {
    if (!isAdmin) return;
    let canceled = false;
    const loadYoutubeAccounts = async () => {
      try {
        const response = await api.get<{
          data: Array<{ id: number; name: string }>;
        }>("/app/lives/youtube-accounts");
        const rows = Array.isArray(response.data?.data)
          ? response.data.data
          : [];
        const normalized: YoutubeAccountOption[] = rows
          .map((r: any) => ({ id: Number(r.id), name: String(r.name ?? r.id) }))
          .filter((r) => Number.isFinite(r.id) && !!r.name);
        normalized.sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
        );
        if (!canceled) setYoutubeAccounts(normalized);
      } catch {
        if (!canceled) setYoutubeAccounts([]);
      }
    };
    void loadYoutubeAccounts();
    return () => {
      canceled = true;
    };
  }, [isAdmin]);

  // Quando o estabelecimento muda (já resolvido), segue de novo o mapeamento 1:1 estabelecimento -> playlist.
  useEffect(() => {
    if (!selectedEstablishmentId || !establishmentsReady || !selectedEstablishment)
      return;
    setPlaylistTouched(false);
  }, [selectedEstablishmentId, establishmentsReady, selectedEstablishment]);

  const selectedEstablishmentPlaylistId =
    selectedEstablishment &&
    typeof selectedEstablishment.livePlaylistId === "number"
      ? selectedEstablishment.livePlaylistId
      : null;

  useEffect(() => {
    let canceled = false;
    const loadTemplates = async () => {
      if (!establishmentsReady) return;
      try {
        const response = await api.get<{ data: LiveTemplateRow[] }>(
          "/app/lives/templates",
        );
        const rows = Array.isArray(response.data?.data)
          ? response.data.data
          : [];
        const normalized: LiveTemplateRow[] = rows
          .map((r: any) => ({
            id: Number(r.id),
            name: String(r.name ?? ""),
            placeUuid: String(r.placeUuid ?? ""),
            placeName: String(r.placeName ?? ""),
            deviceName: String(r.deviceName ?? ""),
            channelUuid:
              typeof r.channelUuid === "string"
                ? r.channelUuid
                : (r.channelUuid ?? null),
          }))
          .filter((t) => Number.isFinite(t.id) && t.id > 0);
        if (!canceled) setLiveTemplates(normalized);
      } catch {
        if (!canceled) setLiveTemplates([]);
      }
    };
    void loadTemplates();
    return () => {
      canceled = true;
    };
  }, [establishmentsReady]);

  const templatesForEstablishment = useMemo(() => {
    if (!selectedEstablishmentId) return [];
    const selectedKey = normalizeKeyLoose(
      String(selectedEstablishment?.name ?? ""),
    );
    return liveTemplates.filter((t) => {
      if (String(t.placeUuid) === String(selectedEstablishmentId)) return true;
      if (!selectedKey) return false;
      return normalizeKeyLoose(String(t.placeName ?? "")) === selectedKey;
    });
  }, [liveTemplates, selectedEstablishment?.name, selectedEstablishmentId]);

  const templatesByChannelUuid = useMemo(() => {
    const map = new Map<string, LiveTemplateRow>();
    for (const t of templatesForEstablishment) {
      const key = t.channelUuid ? String(t.channelUuid) : "";
      if (!key) continue;
      if (!map.has(key)) map.set(key, t);
    }
    return map;
  }, [templatesForEstablishment]);

  const selectedCourt = useMemo(() => {
    if (!selectedDeviceId) return null;
    return cameraCourts.find((c) => c.deviceId === selectedDeviceId) ?? null;
  }, [cameraCourts, selectedDeviceId]);

  const selectedTemplate = useMemo(() => {
    const candidateNames = [
      selectedCourt?.channelName,
      selectedCourt?.deviceName,
      selectedDeviceName,
    ]
      .map((v) => (v ? String(v) : ""))
      .filter(Boolean);

    if (selectedChannelUuid) {
      const byChannel = templatesByChannelUuid.get(String(selectedChannelUuid));
      if (byChannel) return byChannel;
    }
    if (candidateNames.length) {
      const needles = candidateNames
        .map((n) => normalizeKeyLoose(n))
        .filter(Boolean);
      const byDevice = templatesForEstablishment.find((t) => {
        const d = normalizeKeyLoose(String(t.deviceName ?? ""));
        const n = normalizeKeyLoose(String(t.name ?? ""));
        return needles.includes(d) || needles.includes(n);
      });
      if (byDevice) return byDevice;
    }
    return null;
  }, [
    selectedChannelUuid,
    selectedCourt?.channelName,
    selectedCourt?.deviceName,
    selectedDeviceName,
    templatesByChannelUuid,
    templatesForEstablishment,
  ]);

  useEffect(() => {
    if (!selectedEstablishmentId) return;
    if (!cameraCourts.length) {
      if (selectedDeviceId) setSelectedDeviceId(null);
      if (selectedDeviceName) setSelectedDeviceName("");
      return;
    }
    const current =
      selectedDeviceId && Number.isFinite(selectedDeviceId)
        ? cameraCourts.find((c) => c.deviceId === selectedDeviceId)
        : null;
    if (current) {
      if (!selectedDeviceName && current.deviceName)
        setSelectedDeviceName(current.deviceName);
      if (!selectedChannelUuid && current.channelUuid)
        setSelectedChannelUuid(String(current.channelUuid));
      return;
    }
    const first = cameraCourts[0];
    setSelectedDeviceId(first.deviceId);
    setSelectedChannelUuid(first.channelUuid ? String(first.channelUuid) : "");
    setSelectedDeviceName(first.channelName ?? first.deviceName ?? "");
  }, [
    cameraCourts,
    selectedChannelUuid,
    selectedDeviceId,
    selectedDeviceName,
    selectedEstablishmentId,
  ]);

  useEffect(() => {
    const estName = selectedEstablishment?.name ?? "";
    const targetName = selectedDeviceName || selectedCourt?.deviceName || "";
    const next = [estName, targetName].filter(Boolean).join(" · ");
    if (!next) return;
    if (!liveNameTouched) setLiveName(next);
  }, [
    liveNameTouched,
    selectedCourt?.deviceName,
    selectedDeviceName,
    selectedEstablishment,
  ]);

  useEffect(() => {
    const preferred =
      typeof selectedEstablishmentPlaylistId === "number"
        ? selectedEstablishmentPlaylistId
        : "";
    const currentIsNumber =
      typeof selectedLivePlaylistId === "number" &&
      Number.isFinite(selectedLivePlaylistId);

    // Se o usuário já escolheu manualmente, mantém a escolha (a menos que fique inválida).
    if (playlistTouched) {
      if (!currentIsNumber) setSelectedLivePlaylistId(preferred);
      return;
    }

    if (!currentIsNumber || selectedLivePlaylistId !== preferred)
      setSelectedLivePlaylistId(preferred);
  }, [playlistTouched, selectedEstablishmentPlaylistId, selectedLivePlaylistId]);

  const reviewSummary = useMemo(() => {
    const court =
      selectedCourt?.deviceName ||
      selectedCourt?.channelName ||
      selectedDeviceName ||
      "";
    const youtubeAccountName = youtubeAccounts.find(
      (a) =>
        a.id ===
        (typeof selectedYoutubeAccountId === "number"
          ? selectedYoutubeAccountId
          : -1),
    )?.name;
    const playlistName = livePlaylists.find(
      (p) =>
        p.id ===
        (typeof selectedLivePlaylistId === "number"
          ? selectedLivePlaylistId
          : -1),
    )?.name;
    const privacyLabel = intl.formatMessage({
      id:
        privacy === "PUBLIC"
          ? "liveDetails.privacyPublic"
          : privacy === "UNLISTED"
            ? "liveDetails.privacyUnlisted"
            : "liveDetails.privacyPrivate",
    });
    return [
      {
        label: intl.formatMessage({
          id: "liveStreams.ownerScheduleFieldPlaceCourt",
        }),
        value: [selectedEstablishment?.name, court].filter(Boolean).join(" - "),
      },
      {
        label: intl.formatMessage({ id: "liveStreams.name" }),
        value: liveName.trim(),
      },
      isAdmin
        ? {
            label: intl.formatMessage({ id: "liveStreams.youtubeAccount" }),
            value: youtubeAccountName,
          }
        : null,
      {
        label: intl.formatMessage({ id: "liveDetails.playlist" }),
        value: playlistName,
      },
      {
        label: intl.formatMessage({ id: "liveDetails.privacy" }),
        value: privacyLabel,
      },
    ].filter((item): item is NonNullable<typeof item> => item !== null);
  }, [
    intl,
    isAdmin,
    livePlaylists,
    liveName,
    privacy,
    selectedCourt?.channelName,
    selectedCourt?.deviceName,
    selectedDeviceName,
    selectedEstablishment?.name,
    selectedLivePlaylistId,
    selectedYoutubeAccountId,
    youtubeAccounts,
  ]);

  const handleCreate = async () => {
    if (createInFlightRef.current || isCreating) return;
    createInFlightRef.current = true;
    try {
      if (!selectedEstablishmentId || !selectedDeviceId) {
        throw new Error(intl.formatMessage({ id: "liveStreams.noCourts" }));
      }

      const establishmentName =
        selectedEstablishment?.name ?? "Estabelecimento";
      const deviceName =
        selectedCourt?.deviceName ||
        selectedDeviceName ||
        selectedTemplate?.deviceName ||
        "Câmera";

      const resolvedName = liveName.trim()
        ? liveName.trim()
        : `${establishmentName} · ${deviceName}`;
      setIsCreating(true);
      if (
        typeof selectedLivePlaylistId !== "number" ||
        !Number.isFinite(selectedLivePlaylistId) ||
        selectedLivePlaylistId <= 0
      ) {
        throw new Error(
          intl.formatMessage({ id: "liveStreams.playlistRequired" }),
        );
      }

      const ensured = await api.post<{ id: number }>("/app/lives/private", {
        deviceId: selectedDeviceId,
        playlistId: selectedLivePlaylistId,
        name: resolvedName,
        privacy,
        enableAudio,
        musicPath: enableBackgroundMusic ? DEFAULT_BACKGROUND_MUSIC_PATH : null,
        directFinancePayment,
      });
      const liveId = Number(ensured.data?.id);
      if (!Number.isFinite(liveId) || liveId <= 0)
        throw new Error("Invalid create response");

      try {
        localStorage.removeItem(SCHEDULE_DRAFT_KEY);
      } catch {
        // ignore
      }

      navigate(`/transmissoes-ao-vivo/live/${liveId}?tab=detalhes`);
    } catch (err: any) {
      const message = getFriendlyScheduleCreateError(intl, err);
      toast.error(message);
    } finally {
      setIsCreating(false);
      createInFlightRef.current = false;
    }
  };

  return (
    <>
      <DialogContent dividers>
        <Stepper activeStep={scheduleDialogStep} sx={{ mb: 3 }} alternativeLabel>
          <Step>
            <StepLabel>{intl.formatMessage({ id: "liveStreams.stepWhere" })}</StepLabel>
          </Step>
          <Step>
            <StepLabel>{intl.formatMessage({ id: "liveStreams.stepConfiguration" })}</StepLabel>
          </Step>
          <Step>
            <StepLabel>{intl.formatMessage({ id: "liveStreams.stepAudioReview" })}</StepLabel>
          </Step>
        </Stepper>
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2} alignItems="stretch" sx={{ display: scheduleDialogStep === 0 ? "flex" : "none" }}>
            <DeviceSelector
              establishments={establishments}
              selectedEstablishmentId={selectedEstablishmentId}
              onEstablishmentChange={(id) => {
                setPlaylistTouched(false);
                setSelectedLivePlaylistId("");
                setSelectedEstablishmentId(id);
              }}
              cameraOptions={cameraCourts}
              selectedDeviceId={selectedDeviceId}
              onDeviceChange={(device: CameraOption | null) => {
                setSelectedDeviceId(device?.deviceId ?? null);
                setSelectedChannelUuid(
                  device?.channelUuid ? String(device.channelUuid) : "",
                );
                setSelectedDeviceName(
                  device?.deviceName
                    ? String(device.deviceName)
                    : device?.channelName
                      ? String(device.channelName)
                      : "",
                );
              }}
              loading={templatesLoading}
            />

            <Grid item xs={12}>
              <Input
                size="small"
                label={intl.formatMessage({ id: "liveStreams.name" })}
                value={liveName}
                onChange={(e) => {
                  setLiveNameTouched(true);
                  setLiveName(e.target.value);
                }}
                fullWidth
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} alignItems="stretch" sx={{ display: scheduleDialogStep === 1 ? "flex" : "none" }}>
            {isAdmin ? (
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={youtubeAccounts}
                  filterOptions={youtubeAccountFilter}
                  value={
                    youtubeAccounts.find(
                      (a) =>
                        a.id ===
                        (typeof selectedYoutubeAccountId === "number"
                          ? selectedYoutubeAccountId
                          : -1),
                    ) ?? null
                  }
                  onChange={(_, next) => {
                    setSelectedYoutubeAccountId(next?.id ?? "");
                    // Esvazia já: senão a lista da conta anterior (ou "todas") fica visível até o refetch.
                    setLivePlaylists([]);
                    setPlaylistTouched(true);
                    setSelectedLivePlaylistId("");
                  }}
                  getOptionLabel={(opt) => opt.name}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  label={intl.formatMessage({ id: "liveStreams.youtubeAccount" })}
                  placeholder={intl.formatMessage({ id: "liveStreams.youtubeAccountPlaceholder" })}
                  slotProps={{ popper: { sx: { zIndex: theme.zIndex.modal + 20 } } }}
                  ListboxProps={{ style: { maxHeight: 320 } }}
                />
              </Grid>
            ) : null}

            <Grid item xs={12} md={6}>
              <Autocomplete
                options={livePlaylists}
                filterOptions={playlistFilter}
                value={
                  livePlaylists.find(
                    (p) =>
                      p.id ===
                      (typeof selectedLivePlaylistId === "number"
                        ? selectedLivePlaylistId
                        : -1),
                  ) ?? null
                }
                onChange={(_, next) => {
                  setPlaylistTouched(true);
                  setSelectedLivePlaylistId(next?.id ?? "");
                }}
                getOptionLabel={(opt) => opt.name}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                label={intl.formatMessage({ id: "liveDetails.playlist" })}
                placeholder={intl.formatMessage({ id: "liveAdmin.searchPlaylist" })}
                error={!selectedEstablishmentPlaylistId}
                helperText={
                  !selectedEstablishmentPlaylistId
                    ? intl.formatMessage({ id: "liveStreams.noPlaylistForEstablishment" })
                    : undefined
                }
                slotProps={{ popper: { sx: { zIndex: theme.zIndex.modal + 20 } } }}
                ListboxProps={{ style: { maxHeight: 320 } }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Select
                label={intl.formatMessage({ id: "liveDetails.privacy" })}
                value={privacy}
                onChange={(e) => {
                  const next = String(e.target.value) as Privacy;
                  setPrivacy(next);
                }}
                MenuProps={selectMenuProps as any}
                helperText={intl.formatMessage({
                  id:
                    privacy === "PUBLIC"
                      ? "liveDetails.privacyPublicHint"
                      : privacy === "UNLISTED"
                        ? "liveDetails.privacyUnlistedHint"
                        : "liveDetails.privacyPrivateHint",
                })}
              >
                <MenuItem value="PUBLIC">
                  {intl.formatMessage({ id: "liveDetails.privacyPublic" })}
                </MenuItem>
                <MenuItem value="UNLISTED">
                  {intl.formatMessage({ id: "liveDetails.privacyUnlisted" })}
                </MenuItem>
                <MenuItem value="PRIVATE">
                  {intl.formatMessage({ id: "liveDetails.privacyPrivate" })}
                </MenuItem>
              </Select>
            </Grid>
          </Grid>

          <Grid container spacing={2} alignItems="stretch" sx={{ display: scheduleDialogStep === 2 ? "flex" : "none" }}>
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                  {intl.formatMessage({ id: "liveStreams.reviewTitle" })}
                </Typography>
                <Stack spacing={0.5}>
                  {reviewSummary.map((item) => (
                    <Stack key={item.label} direction="row" justifyContent="space-between" spacing={2}>
                      <Typography variant="body2" color="text.secondary">
                        {item.label}
                      </Typography>
                      <Typography variant="body2" fontWeight={600} sx={{ textAlign: "right" }}>
                        {item.value || "-"}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Stack direction="column" alignItems="flex-start" gap={2}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
                  <Checkbox
                    label={intl.formatMessage({ id: "liveDetails.enableAudio" })}
                    checkboxProps={{
                      checked: enableAudio,
                      onChange: (e) => setEnableAudio(e.target.checked),
                    }}
                  />
                  <Checkbox
                    label={intl.formatMessage({ id: "liveDetails.backgroundMusic" })}
                    checkboxProps={{
                      checked: enableBackgroundMusic,
                      onChange: (e) => setEnableBackgroundMusic(e.target.checked),
                    }}
                  />
                  <Checkbox
                    label={intl.formatMessage({ id: "liveStreams.directFinancePayment" })}
                    checkboxProps={{
                      checked: directFinancePayment,
                      onChange: (e) => setDirectFinancePayment(e.target.checked),
                    }}
                  />
                </Stack>
                <Tooltip
                  title={
                    isCreating
                      ? intl.formatMessage({ id: "liveStreams.disabledCreating" })
                      : templatesLoading
                        ? intl.formatMessage({ id: "liveStreams.disabledLoading" })
                        : !isLocalhost && !selectedEstablishmentId
                          ? intl.formatMessage({ id: "liveStreams.disabledSelectEstablishment" })
                          : !isLocalhost && !selectedDeviceId
                            ? intl.formatMessage({ id: "liveStreams.disabledSelectCamera" })
                            : !isLocalhost &&
                                (typeof selectedLivePlaylistId !== "number" ||
                                  selectedLivePlaylistId <= 0)
                              ? intl.formatMessage({ id: "liveStreams.disabledSelectPlaylist" })
                              : ""
                  }
                >
                  <Box sx={{ width: "100%", display: "flex", justifyContent: "flex-end" }}>
                    <LoadingButton
                      variant="contained"
                      color="success"
                      onClick={() => void handleCreate()}
                      fullWidth={false}
                      disabled={
                        isCreating ||
                        templatesLoading ||
                        (!isLocalhost && !selectedEstablishmentId) ||
                        (!isLocalhost && !selectedDeviceId) ||
                        (!isLocalhost &&
                          (typeof selectedLivePlaylistId !== "number" ||
                            selectedLivePlaylistId <= 0))
                      }
                      sx={{ minWidth: { xs: "100%", sm: 280 }, fontWeight: fontWeights.bold }}
                    >
                      {intl.formatMessage({ id: "liveStreams.createAndPay" })}
                    </LoadingButton>
                  </Box>
                </Tooltip>
              </Stack>
            </Grid>
          </Grid>
        </Box>

        {scheduleDialogStep < 2 ? (
          <Stack direction="row" justifyContent="space-between" sx={{ mt: 3 }}>
            <LoadingButton
              variant="text"
              onClick={() => setScheduleDialogStep((s) => Math.max(0, s - 1))}
              disabled={scheduleDialogStep === 0}
              fullWidth={false}
            >
              {intl.formatMessage({ id: "common.back" })}
            </LoadingButton>
            <LoadingButton
              variant="contained"
              onClick={() => setScheduleDialogStep((s) => Math.min(2, s + 1))}
              disabled={
                scheduleDialogStep === 0
                  ? !scheduleStepWhereWhenValid
                  : !scheduleStepConfigurationValid
              }
              fullWidth={false}
            >
              {intl.formatMessage({ id: "common.continue" })}
            </LoadingButton>
          </Stack>
        ) : (
          <Stack direction="row" justifyContent="flex-start" sx={{ mt: 3 }}>
            <LoadingButton
              variant="text"
              onClick={() => setScheduleDialogStep((s) => Math.max(0, s - 1))}
              fullWidth={false}
            >
              {intl.formatMessage({ id: "common.back" })}
            </LoadingButton>
          </Stack>
        )}
      </DialogContent>
    </>
  );
}
